import { Dialog } from 'evergreen-ui';
import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import {
  listOffboardings,
  startOffboarding,
  updateOffboarding,
  OffboardingRecord,
} from '../../../network/offboarding';

type Props = {
  isOpen: boolean;
  onClose(): void;
  user: any;
};

function toDateInputValue(d: Date) {
  return d.toISOString().slice(0, 10);
}

const DEFAULT_CHECKLIST: OffboardingRecord['details']['checklist'] = [
  { id: 'handover', title: 'Handover completed', owner: 'manager', completed: false },
  { id: 'equipment', title: 'Company equipment returned', owner: 'it', completed: false },
  { id: 'access', title: 'Access removal scheduled', owner: 'it', completed: false },
  { id: 'exit_interview', title: 'Exit interview completed', owner: 'hr', completed: false },
];

export function OffboardUserDialog({ isOpen, onClose, user }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [existing, setExisting] = useState<OffboardingRecord | null>(null);
  const [lastWorkingDay, setLastWorkingDay] = useState<string>(
    toDateInputValue(new Date()),
  );
  const [reason, setReason] = useState<string>('');
  const [status, setStatus] = useState<OffboardingRecord['details']['status']>('scheduled');
  const [checklist, setChecklist] = useState<OffboardingRecord['details']['checklist']>(
    DEFAULT_CHECKLIST,
  );

  const userId = useMemo(() => user?.id, [user]);

  useEffect(() => {
    if (!isOpen || !userId) return;
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        const result = await listOffboardings({ userId: String(userId) });
        const record = result?.data?.[0] || null;
        if (cancelled) return;
        setExisting(record);
        if (record?.details?.lastWorkingDay) {
          setLastWorkingDay(toDateInputValue(new Date(record.details.lastWorkingDay)));
        }
        if (record?.details?.reason) setReason(record.details.reason);
        if (record?.details?.status) setStatus(record.details.status);
        if (record?.details?.checklist?.length) setChecklist(record.details.checklist);
        else setChecklist(DEFAULT_CHECKLIST);
      } catch {
        if (cancelled) return;
        setExisting(null);
        setStatus('scheduled');
        setChecklist(DEFAULT_CHECKLIST);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  const toggleChecklistItem = (id: string) => {
    setChecklist((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const nextCompleted = !item.completed;
        return {
          ...item,
          completed: nextCompleted,
          completedAt: nextCompleted ? new Date().toISOString() : undefined,
        };
      }),
    );
  };

  const handleSubmit = async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      if (existing?.system?.id) {
        const result = await updateOffboarding(existing.system.id, {
          lastWorkingDay,
          reason,
          status,
          checklist,
        });
        setExisting(result?.data || existing);
      } else {
        const result = await startOffboarding({
          userId: String(userId),
          lastWorkingDay,
          reason,
          checklist,
        });
        setExisting(result?.data || null);
      }
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      isShown={isOpen}
      onCloseComplete={onClose}
      hasFooter={false}
      hasHeader={false}
    >
      <div className="flex flex-col">
        <div className="mb-4 flex flex-row justify-between pt-6">
          <div className="rounded-[10px] bg-[#FEE9E9] p-3 shadow-sm">
            <ArrowRightOnRectangleIcon color="#F04438" className="w-6" />
          </div>
          <XMarkIcon className="h-6 w-6 cursor-pointer" onClick={onClose} />
        </div>

        <div className="mb-3 flex flex-col gap-1">
          <div className="text-lg font-semibold">
            Offboard {user?.full_name || 'employee'}
          </div>
          <div className="text-sm font-normal text-[#475467]">
            Access will be deactivated at the end of the selected last working day.
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <label className="text-sm font-medium text-gray-700">
            Last working day
            <input
              type="date"
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={lastWorkingDay}
              onChange={(e) => setLastWorkingDay(e.target.value)}
              disabled={isLoading}
            />
          </label>

          <label className="text-sm font-medium text-gray-700">
            Reason (optional)
            <input
              type="text"
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              placeholder="e.g. Resignation"
            />
          </label>

          <label className="text-sm font-medium text-gray-700">
            Status
            <select
              className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              disabled={isLoading}
            >
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In progress</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>

          <div className="mt-2 rounded-md border border-gray-200 bg-white p-3">
            <div className="mb-2 text-sm font-semibold text-gray-900">
              Offboarding checklist
            </div>
            <div className="space-y-2">
              {checklist.map((item) => (
                <label
                  key={item.id}
                  className="flex cursor-pointer items-start justify-between gap-3 rounded-md px-2 py-1 hover:bg-gray-50"
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300"
                      checked={!!item.completed}
                      onChange={() => toggleChecklistItem(item.id)}
                      disabled={isLoading || status === 'cancelled'}
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.title}
                      </div>
                      <div className="text-xs text-gray-500">
                        Owner: {item.owner}
                        {item.completedAt ? ` • Completed ${String(item.completedAt).slice(0, 10)}` : ''}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-6 mt-6 flex flex-row justify-between gap-3">
          <button
            className="w-full rounded-lg border border-[#D0D5DD] bg-white px-[18px] py-[10px] text-base font-semibold text-[#344054] shadow-sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="w-full rounded-lg bg-[#DC2626] px-[18px] py-[10px] text-base font-semibold text-white shadow-sm "
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {existing ? 'Update Offboarding' : 'Start Offboarding'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

